<template>
	<div v-if="isOpen" id="whois-modal-container" @click="containerClick">
		<div id="whois-modal" role="dialog" aria-modal="true">
			<button
				id="whois-modal-close"
				type="button"
				aria-label="Затвори"
				@click="close"
			>
				×
			</button>

			<p v-if="whois" class="whois-modal-header">
				<Username :user="{nick: whois.nick}" />
				<span v-if="whois.whowas"> е офлайн, последна информация:</span>
			</p>

			<dl v-if="whois" class="whois">
				<template v-if="whois.account">
					<dt>Логнат:</dt>
					<dd>{{ whois.account }}</dd>
				</template>

				<dt>Маска:</dt>
				<dd class="hostmask">
					<ParsedMessage
						v-if="network"
						:network="network"
						:text="whois.ident + '@' + whois.hostname"
					/>
				</dd>

				<template v-if="whois.actual_hostname">
					<dt>Хост:</dt>
					<dd class="hostmask">
						<a
							:href="'https://ipinfo.io/' + whois.actual_ip"
							target="_blank"
							rel="noopener"
							>{{ whois.actual_ip }}</a
						>
						<i v-if="whois.actual_hostname != whois.actual_ip"
							>({{ whois.actual_hostname }})</i
						>
					</dd>
				</template>

				<template v-if="whois.actual_username">
					<dt>Реално потребителско име:</dt>
					<dd>{{ whois.actual_username }}</dd>
				</template>

				<template v-if="whois.real_name">
					<dt>Име:</dt>
					<dd>
						<ParsedMessage v-if="network" :network="network" :text="whois.real_name" />
					</dd>
				</template>

				<template v-if="whois.registered_nick">
					<dt>Ник:</dt>
					<dd>{{ whois.registered_nick }}</dd>
				</template>

				<template v-if="whois.channels">
					<dt>Канали:</dt>
					<dd>
						<ParsedMessage v-if="network" :network="network" :text="whois.channels" />
					</dd>
				</template>

				<template v-if="specialParts.length">
					<dt>Специален:</dt>
					<dd>
						<template v-for="(part, idx) in specialParts" :key="idx">
							<span v-if="part.label" class="whois-sublabel">{{ part.label }}: </span
							>{{ part.value }}<span v-if="idx < specialParts.length - 1">, </span>
						</template>
					</dd>
				</template>

				<template v-if="whois.operator">
					<dt>Оператор:</dt>
					<dd>{{ whois.operator }}</dd>
				</template>

				<template v-if="whois.helpop">
					<dt>Помощ:</dt>
					<dd>Да</dd>
				</template>

				<template v-if="whois.bot">
					<dt>Е бот:</dt>
					<dd>Да</dd>
				</template>

				<template v-if="whois.away">
					<dt>Отсъстващ:</dt>
					<dd>
						<ParsedMessage v-if="network" :network="network" :text="whois.away" />
					</dd>
				</template>

				<template v-if="whois.certfps">
					<template v-for="certfp in whois.certfps" :key="certfp">
						<dt>Сертификат:</dt>
						<dd>{{ certfp }}</dd>
					</template>
				</template>

				<template v-if="whois.server">
					<dt>Сървър:</dt>
					<dd>{{ whois.server_info || whois.server }}</dd>
				</template>

				<template v-if="connectedParts.length">
					<dt>Свързан:</dt>
					<dd>
						<template v-for="(part, idx) in connectedParts" :key="idx">
							<span v-if="part.label" class="whois-sublabel">{{ part.label }}: </span
							>{{ part.value }}<span v-if="idx < connectedParts.length - 1"> · </span>
						</template>
					</dd>
				</template>
			</dl>
		</div>
	</div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, onUnmounted, ref} from "vue";
import eventbus from "../js/eventbus";
import localetimeShort from "../js/helpers/localetimeShort";
import {ClientNetwork} from "../js/types";
import ParsedMessage from "./ParsedMessage.vue";
import Username from "./Username.vue";

type WhoisOpenPayload = {
	whois: any;
	network: ClientNetwork | null;
};

export default defineComponent({
	name: "WhoisModal",
	components: {
		ParsedMessage,
		Username,
	},
	setup() {
		const isOpen = ref(false);
		const whois = ref<any>(null);
		const network = ref<ClientNetwork | null>(null);

		const close = () => {
			isOpen.value = false;
			whois.value = null;
		};

		const containerClick = (event: MouseEvent) => {
			if (event.currentTarget === event.target) {
				close();
			}
		};

		const openHandler = (data: WhoisOpenPayload) => {
			whois.value = data.whois;
			network.value = data.network;
			isOpen.value = true;
		};

		type LabelValuePart = {label: string | null; value: string};

		const specialParts = computed<LabelValuePart[]>(() => {
			if (!whois.value) {
				return [];
			}

			const parts: LabelValuePart[] = [];

			if (Array.isArray(whois.value.special)) {
				const encodingEntry = whois.value.special.find((s: string) =>
					s.toLowerCase().includes("encoding")
				);

				if (encodingEntry) {
					const value = encodingEntry.split(":").pop()?.trim();

					if (value) {
						parts.push({label: "encoding", value});
					}
				}
			}

			if (whois.value.secure) {
				parts.push({label: "SSL", value: "Да"});
			}

			if (whois.value.modes) {
				const match = /\+\S+/.exec(whois.value.modes as string);

				if (match) {
					parts.push({label: "Modes", value: match[0]});
				}
			}

			return parts;
		});

		const connectedParts = computed<LabelValuePart[]>(() => {
			if (!whois.value) {
				return [];
			}

			const parts: LabelValuePart[] = [];

			if (whois.value.logonTime) {
				parts.push({label: null, value: localetimeShort(whois.value.logonTime as Date)});
			}

			if (whois.value.idle) {
				parts.push({
					label: "Неактивен",
					value: localetimeShort(whois.value.idleTime as Date),
				});
			}

			return parts;
		});

		onMounted(() => {
			eventbus.on("whois:open", openHandler);
			eventbus.on("escapekey", close);
		});

		onUnmounted(() => {
			eventbus.off("whois:open", openHandler);
			eventbus.off("escapekey", close);
		});

		return {
			isOpen,
			whois,
			network,
			close,
			containerClick,
			specialParts,
			connectedParts,
		};
	},
});
</script>
